import {Component, ElementRef, EventEmitter, OnInit, ViewChild} from '@angular/core';
import {
  Steps,
  TestPassState,
  TestQuestion,
  TestResponseCommands
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/models';
import {
  AudioCheckManager,
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/audio-check-manager';
import {
  CheckItemsManager
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/check-items-manager';
import {
  ScreenCheckManager
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/screen-check-manager';
import {
  NetworkCheckManager
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/network-check-manager';
import {
  SystemCheckManager
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/system-check-manager';
import {
  MemoryCheckManager
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/memory-check-manager';
import {
  VideoCheckManager
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/video-check-manager';
import {
  MicrophoneCheckManager
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/microphone-check-manager';
import {SummaryManager} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/summary-manager';
import {WRTCProviderService} from '@app/modules/wrtc/services/providers/wrtc-provider';

import {AccessType, Role} from '@app/modules/shared/data/constants';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {Store} from '@ngxs/store';
import {HttpClient} from '@angular/common/http';
import {SmartsuppService} from '@app/modules/shared/services/smartsupp.service';
import {ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {DiagnosticsService} from '@app/modules/diagnostics/services/diagnostics.service';
import {ClassroomInitService} from '@app/modules/shared/services/classroom-init.service';
import {Router} from '@angular/router';
import {WrtcService} from '@app/modules/wrtc/services/wrtc.service';
import {DeviceMetadata} from '@app/modules/wrtc/models/device-metadata';

@Component({
  selector: 'app-diagnostics',
  templateUrl: './diagnostics.component.html',
  styleUrls: ['./diagnostics.component.css']
})

export class DiagnosticsComponent implements OnInit {
  public testPassState = TestPassState;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public Steps = Steps;
  public isTestsRunning = false;
  public micValue;
  public isAllTestsSucceed = true;
  public runningStepIndex = 0;
  public viewStepIndex = 0;
  public reachedMaxTestIndex = 0;
  public itemsArray: CheckItemsManager [] = [];

  public audioInputDevices: DeviceMetadata[] = [];
  public videoInputDevices: DeviceMetadata[] = [];
  public audioOutputDevices: MediaDeviceInfo[] = [];
  public selectedMicId: string;
  public selectedCamId: string;
  public selectedSpeakerId: string;

  public preparationQuestion: TestQuestion = {
    isExist: false,
    text: '',
    answerOptions: [],
  };
  public localUser: LocalUser;
  public smartsuppUsername: string;
  public isAllTestsFinished = false;
  public classroom: ClassroomStateModel;
  public recordId;
  public isLoaded = false;
  public isMicPermitted = true;
  private voiceMeterInterval: NodeJS.Timeout;

  @ViewChild('videoWrapper')
  private videoWrapper: ElementRef<HTMLVideoElement>;
  @ViewChild('audioTest')
  private audioTestRef: ElementRef<HTMLAudioElement>;
  public stream: MediaStream;
  public audio: HTMLAudioElement;
  public audioFilePath = 'assets/audio/mic-check-sound.mp3';

  constructor(
    private http: HttpClient,
    public wrtcService: WrtcService,
    private smartsuppService: SmartsuppService,
    private wrtcProviderService: WRTCProviderService,
    private store: Store,
    private diagnosticService: DiagnosticsService,
    private classroomInitService: ClassroomInitService,
    private router: Router
  ) {
    this.listenStoreChanges();
    this.audio = new Audio(this.audioFilePath);
  }

  public async ngOnInit(): Promise<void> {
    this.diagnosticService.setPassed();
    this.diagnosticService.createRecord().subscribe(async(response: any) => {
      this.recordId = response.data;
      this.initiateSteps();
      await this.loadDevices();
      this.isLoaded = true;
    });
  }

  goToClassroom(): void {
    clearInterval(this.voiceMeterInterval);
    if (this.classroom.isDnDEnabled && !this.localUser.isCurrentClassParticipant) {
      let navigateURL: string = `/${this.classroom.roomNum}/classroomClosed`;
      if (this.classroom.currentClass) {
        navigateURL += `?classId=${this.classroom.currentClass.groupId}`;
      }
      this.router.navigateByUrl(navigateURL).then();
    } else {
      this.classroomInitService.navigateToHome();
    }
  }

  listenStoreChanges(): void {
    this.store.select(LocalUserState.getLocalUser).subscribe((localUser: LocalUser) => {
      const includeRole = localUser.accessType !== null && localUser.accessType !== undefined;
      this.smartsuppUsername = this.smartsuppService.buildUsername(includeRole);
      this.localUser = localUser;
    });
    this.store.select(ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.classroom = classroom;
    });
  }

  async checkMicTapResponse(): Promise<void> {
    if (this.voiceMeterInterval) {
      clearInterval(this.voiceMeterInterval);
    }

    const emitter = new EventEmitter<number>();

    const constraints: any = { audio: true };
    if (this.selectedMicId) {
      constraints.audio = { deviceId: { exact: this.selectedMicId } };
    }
    this.stream = await navigator.mediaDevices.getUserMedia(constraints);

    const tracks = this.stream.getAudioTracks();

    const audioContext: AudioContext = new (window.AudioContext || window.webkitAudioContext)();
    const analyser: AnalyserNode = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.5;
    audioContext.resume().then(() => {
      const stream: MediaStream = new MediaStream(tracks);
      const source: MediaStreamAudioSourceNode = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      this.voiceMeterInterval = setInterval(() => {
        this.wrtcProviderService.emmitVoiceLevel(source, analyser, stream, emitter);
      }, 100);

      emitter.subscribe(value => {
        this.micValue = value;
        if (this.viewStepIndex === Steps.MIC && this.itemsArray[this.viewStepIndex].currentStepIndex === 1 && this.micValue > 0.20) {
          this.itemsArray[this.viewStepIndex].steps[1].testQuestion.answerOptions[1].isBlock = false;
        }
      });
    });

    // const emitter = new EventEmitter<number>();
    // this.wrtcProviderService.getLocalUserAudioTrack(AccessType.STUDENT, false).subscribe(track => {
    //   const audioContext: AudioContext = new (window.AudioContext || window.webkitAudioContext)();
    //   const analyser: AnalyserNode = audioContext.createAnalyser();
    //   analyser.fftSize = 1024;
    //   analyser.smoothingTimeConstant = 0.5;
    //   audioContext.resume().then(() => {
    //     const stream: MediaStream = new MediaStream([track.mediaStreamTrack]);
    //     const source: MediaStreamAudioSourceNode = audioContext.createMediaStreamSource(stream);
    //     source.connect(analyser);
    //
    //     this.voiceMeterInterval = setInterval(() => {
    //       this.wrtcProviderService.emmitVoiceLevel(source, analyser, stream, emitter);
    //     }, 100);
    //
    //     emitter.subscribe(value => {
    //       this.micValue = value;
    //       if (this.viewStepIndex === Steps.MIC && this.itemsArray[this.viewStepIndex].currentStepIndex === 1 && this.micValue > 0.20) {
    //         this.itemsArray[this.viewStepIndex].steps[1].testQuestion.answerOptions[1].isBlock = false;
    //       }
    //     });
    //   });
    // });
  }

  deleteStep(itemName: string): void {
    if (this.localUser.accessType === AccessType.STUDENT) {
      this.itemsArray = this.itemsArray.filter(a => a.name !== itemName);
    }
  }

  initiateSteps(): void {
    this.itemsArray.push(new ScreenCheckManager(this.localUser.role));
    this.itemsArray.push(new NetworkCheckManager(this.localUser.role, this.wrtcService));
    this.itemsArray.push(new SystemCheckManager(this.localUser.role));
    this.itemsArray.push(new MemoryCheckManager(this.localUser.role));
    this.itemsArray.push(new AudioCheckManager(this.localUser.role));
    this.itemsArray.push(new VideoCheckManager(this.localUser.role));
    this.itemsArray.push(new MicrophoneCheckManager(this.localUser.role, this.wrtcProviderService));
    this.itemsArray.push(new SummaryManager(this.localUser.role));
  }

  runTests(): void {
    this.isTestsRunning = true;
    if (!this.isMicPermitted && this.itemsArray[this.runningStepIndex] === this.itemsArray[Steps.MIC]) {
      this.runningStepIndex++;
      this.reachedMaxTestIndex = this.runningStepIndex;
    }
    this.viewStepIndex = this.runningStepIndex;
    const currentCheckManager = this.itemsArray[this.runningStepIndex];
    if (!currentCheckManager.isBlocked) {
      currentCheckManager.runTests(testResult => {
        switch (testResult.command) {
          case TestResponseCommands.NEXT: {
            this.saveResults();
            if (!this.isAllTestsFinished) {
              if (this.runningStepIndex < this.itemsArray.length - 1) {
                this.runningStepIndex++;
                // no need
                if (this.runningStepIndex > this.reachedMaxTestIndex) {
                  this.reachedMaxTestIndex = this.runningStepIndex;
                }
                //this is to prevent run test in summary case
                if (this.runningStepIndex !== this.itemsArray.length) {
                  this.viewStepIndex = this.runningStepIndex;
                  this.runTests();
                }
              } else {
                this.updateIsAllTestSucceed();
                this.isAllTestsFinished = true;
              }
            }
            break;
          }
          case TestResponseCommands.REQUEST_QUESTION: {
            // this.showQuestion(testResult.data);
            break;
          }
          case TestResponseCommands.VIDEO_MIC_IS_BLOCK: {
            this.itemsArray[Steps.MIC].setBlocked(true);
            break;
          }
          case TestResponseCommands.REQUEST_VIDEO: {
            this.playVideo();
            this.itemsArray[Steps.MIC].steps[1].isHidden = false;
            break;
          }
          case TestResponseCommands.REQUEST_MIC: {
            this.checkMicTapResponse();
            this.itemsArray[Steps.MIC].steps[0].name = 'Checking microphone';
            this.itemsArray[Steps.MIC].steps[1].isHidden = this.isAllAudioTestPassed();
            break;
          }
          case TestResponseCommands.GUEST: {
           this.guestHandler();
            break;
          }
          case TestResponseCommands.SHOW_ERROR: {
            this.saveResults();
            console.log('Test fail ');
            break;
          }
        }
      });
    } else {
      if (this.runningStepIndex < this.itemsArray.length - 1) {
        this.runningStepIndex++;

        if (this.runningStepIndex > this.reachedMaxTestIndex) {
          this.reachedMaxTestIndex = this.runningStepIndex;
        }
        this.viewStepIndex = this.runningStepIndex;
        this.runTests();
      }
    }

  }

  isAllAudioTestPassed(): boolean {
    if(!this.itemsArray[Steps.AUDIO].steps[0].testQuestion.answerOptions[2].isDisabled){
      return true;
    }
    if (this.itemsArray[Steps.AUDIO].isAtLeastOneTestFail && this.reachedMaxTestIndex < this.itemsArray.length - 1) {
      return true;
    }
    return false;
  }

  hideVideoAndAudioSteps(chosenOption: string) {
    if (this.itemsArray[Steps.VIDEO].steps[0] === this.itemsArray[this.viewStepIndex].steps[this.itemsArray[this.viewStepIndex].currentStepIndex] &&
      chosenOption === 'no' ||
      this.itemsArray[Steps.AUDIO].steps[0] === this.itemsArray[this.viewStepIndex].steps[this.itemsArray[this.viewStepIndex].currentStepIndex] &&
      chosenOption === 'no' ||
      this.itemsArray[Steps.AUDIO].steps[1] === this.itemsArray[this.viewStepIndex].steps[this.itemsArray[this.viewStepIndex].currentStepIndex] &&
      chosenOption === 'no') {
      this.itemsArray[this.viewStepIndex].steps[2].isHidden = true;
    }
  }
  guestHandler(){
    if(this.localUser.isGuest){
      this.itemsArray[Steps.AUDIO].steps[0].testQuestion.answerOptions[2].isDisabled=false;
    }
  }

  handleTestPreparationQuestionClick(chosenOption): void {
    this.hideVideoAndAudioSteps(chosenOption);
    this.itemsArray[this.viewStepIndex].setCurrentStepPayload(chosenOption);
    // run normally
    if (this.viewStepIndex === this.runningStepIndex) {
      this.runTests();
    } else {
      // retry case
      this.runTestsWithoutMovingForward(this.viewStepIndex);
    }
  }

  retry(index?: number): void {
    //todo why?
    if (Steps.VIDEO === this.viewStepIndex && this.itemsArray[Steps.MIC].steps[0].testResult.status === TestPassState.FAILED) {
      this.itemsArray[Steps.MIC].steps[0].testResult.status = TestPassState.PENDING;
    }
    this.viewStepIndex = index;
    this.itemsArray[this.viewStepIndex].resetManager();
    this.runTestsWithoutMovingForward(this.viewStepIndex);
    this.loadDevices();
  }

  runTestsWithoutMovingForward(stepIndex): void {
    const currentCheckManager = this.itemsArray[stepIndex];
    currentCheckManager.runTests(testResult => {
      switch (testResult.command) {
        case TestResponseCommands.NEXT: {
          this.updateIsAllTestSucceed();
          this.saveResults();
          // if after retry user gave access to video make microphone manage enabled.
          if (stepIndex === Steps.VIDEO && this.itemsArray[this.viewStepIndex].currentStepIndex === 2) {
            this.itemsArray[Steps.MIC].setBlocked(false);
            this.runningStepIndex = ++this.viewStepIndex;
            this.runTests();
          }
          // show go to summary button in case when all steps are processed
          if (this.isAllTestsFinished) {
            this.itemsArray[stepIndex].needToShowSummaryButton = true;
          }
          // run next test
          if (this.viewStepIndex === this.runningStepIndex) {
            this.runningStepIndex++;
            this.runTests();
          }

          break;
        }
        case TestResponseCommands.REQUEST_QUESTION: {
          // this.showQuestion(testResult.data);
          break;
        }
        case TestResponseCommands.REQUEST_VIDEO: {
          this.playVideo();
          this.itemsArray[Steps.MIC].steps[1].isHidden = false;
          break;
        }
        case TestResponseCommands.REQUEST_MIC: {
          this.checkMicTapResponse();
          this.itemsArray[Steps.MIC].steps[0].name = 'Checking microphone';
          this.itemsArray[Steps.MIC].steps[1].isHidden = this.isAllAudioTestPassed();
          break;
        }
        case TestResponseCommands.SHOW_ERROR: {
          this.saveResults();
          console.log('Test fail ');
          break;
        }
      }
    });
  }

  skip(): void {
    this.itemsArray[this.viewStepIndex].setSkipped(true);
    const doseAllStepsProcessed = this.itemsArray[this.viewStepIndex].doesAllStepsProcessed();
    if (this.runningStepIndex === this.viewStepIndex) {
      this.runningStepIndex++;
      //no need
      if (this.runningStepIndex > this.reachedMaxTestIndex) {
        this.reachedMaxTestIndex = this.runningStepIndex;
      }
      this.viewStepIndex++;
    }

    if (this.runningStepIndex === this.viewStepIndex) {
      this.runTests();
    } else if (!doseAllStepsProcessed) {
      this.runTestsWithoutMovingForward(this.viewStepIndex);
    }

  }

  switchTestManager(index: number): void {
    this.viewStepIndex = index;
    this.updateIsAllTestSucceed();
    if (this.viewStepIndex === Steps.VIDEO) {
      this.playVideo();
    }
  }

  updateIsAllTestSucceed(): void {
    for (const item of this.itemsArray) {
      if (item.testsPassState === TestPassState.FAILED) {
        this.isAllTestsSucceed = false;
        break;
      } else {
        this.isAllTestsSucceed = true;
      }
    }
  }

  async playVideo(): Promise<void>{
    try {
      const constraints: any = { video: true };
      if (this.selectedCamId) {
        constraints.video = { deviceId: { exact: this.selectedCamId } };
      }
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);

      const videoElement = document.createElement('video');
      videoElement.height = 180;
      videoElement.width = 240;
      videoElement.srcObject = this.stream;
      videoElement.autoplay = true;

      videoElement.addEventListener('loadedmetadata', () => {
        this.videoWrapper.nativeElement.innerHTML = '';
        this.videoWrapper.nativeElement.appendChild(videoElement);
      });

      videoElement.addEventListener('error', (error) => {
        this.videoWrapper.nativeElement.innerHTML = '';
        this.videoWrapper.nativeElement.appendChild(videoElement);
      });
    } catch (error) {
      console.error('Error starting camera:', error);
    }
  }

  backToSummary(index: number, currentIndex: number): void {
    this.viewStepIndex = index;
    this.updateIsAllTestSucceed();
  }

  saveResults() {
    let messages = '';
    for (let i = 0; i < this.itemsArray.length - 1; i++) {
      messages += this.itemsArray[i].generateMessage();
    }
    this.diagnosticService.writeDiagnosticsInfo(this.recordId, messages).subscribe();

  }

  protected async onSelectSpeaker(deviceId: string): Promise<void> {
    this.selectedSpeakerId = deviceId;
    localStorage.setItem('audio_output_device_id', deviceId);
    await this.applyAudioSink();
  }

  protected async onSelectMicrophone(deviceId: string): Promise<void> {
    this.selectedMicId = deviceId;
    localStorage.setItem('audio_device_id', deviceId);
    this.checkMicTapResponse();
  }

  protected async onSelectCamera(deviceId: string): Promise<void> {
    this.selectedCamId = deviceId;
    localStorage.setItem('video_device_id', deviceId);

    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      await this.playVideo();
    }
  }

  private async applyAudioSink(): Promise<void> {
    const audioEl = this.audioTestRef?.nativeElement as any;
    if (audioEl && typeof audioEl.setSinkId === 'function' && this.selectedSpeakerId) {
      try {
        await audioEl.setSinkId(this.selectedSpeakerId);
      } catch (err) {
        console.warn('setSinkId failed', err);
      }
    }
  }

  private async loadDevices(): Promise<void> {
    this.selectedMicId = localStorage.getItem('audio_device_id');
    this.selectedCamId = localStorage.getItem('video_device_id');
    this.selectedSpeakerId = localStorage.getItem('audio_output_device_id');

    try {
      const meta = await this.wrtcProviderService.getVideoAndAudioDevices();
      this.videoInputDevices = meta.video || [];
      this.audioInputDevices = meta.audio || [];

      if (!this.videoInputDevices.find((device) => device.id === this.selectedCamId)) {
        this.selectedCamId = this.videoInputDevices[0]?.id;
        localStorage.setItem('video_device_id', this.selectedCamId);
      }

      if (!this.audioInputDevices.find((device) => device.id === this.selectedMicId)) {
        this.selectedMicId = this.audioInputDevices[0]?.id;
        localStorage.setItem('audio_device_id', this.selectedMicId);
      }

      if (!this.selectedCamId && this.videoInputDevices.length > 0) {
        this.selectedCamId = this.videoInputDevices.find(d => d.active)?.id || this.videoInputDevices[0].id;
        localStorage.setItem('video_device_id', this.selectedCamId);
      }
      if (!this.selectedMicId && this.audioInputDevices.length > 0) {
        this.selectedMicId = this.audioInputDevices.find(d => d.active)?.id || this.audioInputDevices[0].id;
        localStorage.setItem('audio_device_id', this.selectedMicId);
      }
    } catch (e) {
      console.warn('getVideoAndAudioDevices failed', e);
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.audioOutputDevices = devices.filter(d => d.kind === 'audiooutput' && d.deviceId !== '');

      if (!this.audioOutputDevices.find((device) => device.deviceId === this.selectedSpeakerId)) {
        this.selectedSpeakerId = this.audioOutputDevices[0]?.deviceId;
        localStorage.setItem('audio_output_device_id', this.selectedSpeakerId);
      }

      if (!this.selectedSpeakerId && this.audioOutputDevices.length > 0) {
        this.selectedSpeakerId = this.audioOutputDevices[0].deviceId;
        localStorage.setItem('audio_output_device_id', this.selectedSpeakerId);
      }
      await this.applyAudioSink();
    } catch (e) {
      console.warn('enumerateDevices failed', e);
    }
  }

  protected isSpeakerItem(item: CheckItemsManager): boolean {
    return item instanceof AudioCheckManager;
  }

  protected isCameraItem(item: CheckItemsManager): boolean {
    return item instanceof VideoCheckManager;
  }

  protected isMicrophoneItem(item: CheckItemsManager): boolean {
    return item instanceof MicrophoneCheckManager;
  }

  protected selectedSpeaker(): MediaDeviceInfo | undefined {
    return this.audioOutputDevices.find(d => d.deviceId === this.selectedSpeakerId);
  }

  protected selectedCamera(): DeviceMetadata | undefined {
    return this.videoInputDevices.find(d => d.id === this.selectedCamId);
  }

  protected selectedMicrophone(): DeviceMetadata | undefined {
    return this.audioInputDevices.find(d => d.id === this.selectedMicId);
  }
}
